import React, { useRef, useState, useEffect } from 'react';
import { navigate, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  fontWeights,
  fontSize,
  selectWrapStyles,
  selectStyles,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Container, Svg, Wrapper } from './ui';
import Breadcrumbs from './Breadcrumbs';
import SpaSearch from './SpaSearch';
import SpaFilters from './SpaFilters';
import SpaList from './SpaList';
import LocationMap from './LocationMap';
import RecentlyViewed from './RecentlyViewed';
import Content from './Content';
import ModularBlocks from './ModularBlocks';
import OtherSpaLocations from './OtherSpaLocations';
import TrustpilotWidget from './TrustpilotWidget';
import Loader from './Loader';
import { isObjEmpty, useResults, formatDate } from '../utils';
import searchIcon from '../images/search-icon.svg';
import filterIcon from '../images/filter-icon.svg';
import listIcon from '../images/list-icon.svg';
import mapIcon from '../images/map-icon.svg';

const StyledSpaListing = styled.section``;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  ${minBreakpointQuery.tiny`
    margin-top: 5px;
    margin-bottom: 5px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 10px;
    margin-bottom: 10px;
  `}
`;

const StyledSummary = styled.div`
  ${maxBreakpointQuery.medium`
    position: relative;
    padding: 14px 12px 14px 40px;
    border: 1px solid ${greyColours.lightGrey};
    border-radius: 10px;
    cursor: pointer;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 15px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 20px;
  `}
`;

const StyledSummaryHeading = styled.h1`
  font-weight: ${fontWeights.medium};
  ${fontSize(14)};

  ${minBreakpointQuery.tiny`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.medium`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}
`;

const StyledSummaryText = styled.p`
  ${maxBreakpointQuery.medium`
    margin-top: 4px;
    ${fontSize(14)};
  `}

  ${maxBreakpointQuery.tiny`
    margin-top: 3px;
    ${fontSize(12)};
  `}

  ${minBreakpointQuery.medium`
    ${visuallyHidden()};
  `}
`;

const StyledSummaryIcon = styled(Svg)`
  ${maxBreakpointQuery.medium`
    position: absolute;
    top: 50%;
    left: 12px;
    fill: ${brandColours.primary};
    height: 16px;
    width: 16px;
    transform: translateY(-50%);
  `}

  ${minBreakpointQuery.medium`
    display: none;
  `}
`;

const StyledContent = styled.div`
  position: relative;
  margin: 20px 0;
  overflow: hidden;

  &.show-less-content {
    max-height: 63px;
  }
`;

const StyledContentText = styled.p`
  margin-bottom: 10px;
  ${fontSize(14)};
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${brandColours.quinary};
    border-bottom: 1px solid transparent;
    overflow-wrap: anywhere;
    transition: ${standardTransition('border-color')};

    &:hover {
      border-bottom-color: ${brandColours.quinary};
    }
  }
`;

const StyledContentButton = styled.button`
  padding: 0 4px;
  ${fontSize(14)};
  line-height: 1.5;

  background-color: ${standardColours.white};

  span {
    color: ${brandColours.tertiary};
    text-decoration: underline;
  }

  &.show-less-content {
    position: absolute;
    right: 0;
    bottom: 0;
  }
`;

const StyledSearchWrapper = styled.div`
  &.hide-search-mobile {
    ${maxBreakpointQuery.medium`
      ${visuallyHidden()};
    `}
  }
`;

const StyledFiltersList = styled.div`
  position: relative;
  margin: 20px 0;

  ${minBreakpointQuery.tsmall`
    margin-top: 30px;
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 40px;
    margin-bottom: 40px;
  `}

  &:before {
    content: '';
    position: absolute;
    top: 160px;
    right: 0;
    bottom: 0;
    left: 30%;
    background-color: ${brandColours.quaternary};
    opacity: 0.5;

    ${minBreakpointQuery.small`
      left: 40%;
    `}

    ${minBreakpointQuery.medium`
      top: 100px;
    `}

    ${minBreakpointQuery.large`
      left: 50%;
    `}

    ${minBreakpointQuery.xxlarge`
      top: 110px;
    `}

    ${minBreakpointQuery.xxxxlarge`
      top: 120px;
    `}
  }
`;

const StyledInner = styled.div`
  position: relative;

  ${minBreakpointQuery.medium`
    display: grid;
    grid-template-columns: 1fr 2.8fr;
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    gap: 40px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 60px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 80px;
  `}

  ${minBreakpointQuery.xxxlarge`
    gap: 100px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 120px;
  `}
`;

const StyledFiltersWrapper = styled.div`
  &.hide-filters-mobile {
    ${maxBreakpointQuery.medium`
      ${visuallyHidden()};
    `}
  }
`;

const StyledViews = styled.div`
  ${maxBreakpointQuery.medium`
    margin-top: 30px;
  `}

  ${maxBreakpointQuery.tsmall`
    margin-top: 20px;
  `}
`;

const StyledOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  ${minBreakpointQuery.large`
    margin-bottom: 25px;
  `}
`;

const StyledButtons = styled.div`
  display: flex;
`;

const StyledButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  height: 44px;
  color: ${brandColours.primary};
  ${fontSize(15)};
  line-height: 1.2;
  background-color: ${standardColours.white};
  border: 1px solid ${greyColours.lightGrey};
  border-left: none;
  white-space: nowrap;
  transition: ${standardTransition('background-color')};

  ${minBreakpointQuery.xtiny`
    padding-right: 12px;
    padding-left: 12px;
  `}

  ${minBreakpointQuery.tiny`
    padding-right: 14px;
    padding-left: 14px;
  `}

  ${minBreakpointQuery.small`
    padding-right 16px;
    padding-left 16px;
    ${fontSize(16)};
  `}

  svg {
    height: 16px;
    width: 16px;
    fill: ${brandColours.primary};

    ${minBreakpointQuery.xtiny`
      height: 16px;
      width: 16px;
    `}

    ${minBreakpointQuery.tiny`
      height: 18px;
      width: 18px;
    `}

    ${minBreakpointQuery.small`
      height: 20px;
      width: 20px;
    `}
  }

  span {
    ${minBreakpointQuery.tsmall`
      margin-left: 6px;
    `}

    ${minBreakpointQuery.small`
      margin-left: 8px;
    `}
  }

  &:first-child {
    border-left: 1px solid ${greyColours.lightGrey};
  }

  &.active-button {
    color: ${standardColours.white};
    background-color: ${brandColours.primary};

    svg {
      fill: ${standardColours.white};
    }
  }

  ${({ filters }) => {
    if (filters) {
      return css`
        ${minBreakpointQuery.medium`
          ${visuallyHidden()};
        `}

        span {
          ${maxBreakpointQuery.tsmall`
            ${visuallyHidden()};
          `}
        }
      `;
    } else {
      return css`
        span {
          ${maxBreakpointQuery.small`
            ${visuallyHidden()};
          `}
        }
      `;
    }
  }}
`;

const StyledSort = styled.form`
  display: block;

  &.hide-sort {
    ${visuallyHidden()};
  }
`;

const StyledSelectWrap = styled.label`
  ${selectWrapStyles()};
`;

const StyledLabel = styled.span`
  ${visuallyHidden()};
`;

const StyledSelect = styled.select`
  ${selectStyles()};
  height: 44px;

  ${minBreakpointQuery.mlarge`
    width: 220px;
  `}
`;

const StyledToggle = styled.div`
  display: flex;
  align-items: center;

  ${maxBreakpointQuery.medium`
    flex-basis: 100%;
    margin-top: 15px;
  `}

  ${minBreakpointQuery.medium`
    margin-left: 10px;
  `}

  &.hide-toggle {
    ${visuallyHidden()};
  }
`;

const StyledToggleText = styled.p`
  display: inline-block;
`;

const StyledToggleInfo = styled.span`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  margin-left: 2px;
  height: 17px;
  width: 17px;
  color: ${standardColours.white};
  font-weight: ${fontWeights.bold};
  ${fontSize(12)};
  line-height: 17px;
  text-align: center;
  background-color: ${brandColours.primary};
  border-radius: 50%;
  cursor: pointer;

  &:before,
  &:after {
    position: absolute;
    left: 50%;
    z-index: 1;
    display: none;
    transform: translateX(-50%);
  }

  &:before {
    content: '';
    top: 20px;
    height: 0;
    width: 0;
    border-left: 8px solid ${standardColours.transparent};
    border-right: 8px solid ${standardColours.transparent};
    border-bottom: 8px solid ${brandColours.primary};
  }

  &:after {
    content: attr(data-label);
    top: 28px;
    padding: 10px;
    width: 200px;
    font-weight: ${fontWeights.medium};
    background-color: ${brandColours.primary};
  }

  &:hover {
    &:before,
    &:after {
      display: block;
    }
  }
`;

const StyledToggleWrapper = styled.label`
  position: relative;
  display: inline-block;
  width: 57px;
  height: 29px;
  margin: 0 10px;
`;

const StyledToggleControl = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${brandColours.primary};
  border-radius: 34px;

  &:before {
    position: absolute;
    content: '';
    height: 22px;
    width: 22px;
    left: 4px;
    bottom: 4px;
    background-color: ${standardColours.white};
    transition: ${standardTransition('transform')};
    border-radius: 50%;
  }
`;

const StyledToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked {
    + ${StyledToggleControl} {
      background-color: ${brandColours.primary};

      &:before {
        transform: translateX(26px);
      }
    }
  }
`;

const StyledListWrapper = styled.div`
  &.hide-list {
    ${visuallyHidden()};
  }
`;

const StyledMapWrapper = styled.div`
  &.hide-map {
    ${visuallyHidden()};
  }
`;

const SpaListing = ({
  locationData,
  heading,
  breadcrumbs,
  location,
  content,
  initialResults,
  searchFields,
  options: initialOptions = {},
  defaultSort,
  contentBlocks,
  otherLocations,
  arePackages,
  isSpaGroupListing,
  isGenericSpaListingPage,
}) => {
  const baseUrl = locationData.pathname.replace(/page\/[0-9]+\//, '');

  const sortProfiles = [
    {
      name: 'Price (Low)',
      value: 'PriceLow',
    },
    {
      name: 'Price (High)',
      value: 'PriceHigh',
    },
    {
      name: 'Rating',
      value: 'RatingHigh',
    },
  ];

  let showRadialSearch = false;
  let defaultSortIndex = 0;
  if (location && location.id) {
    initialOptions.locationId = location.id;

    if (
      location.type === 'COUNTRY' ||
      location.type === 'REGION' ||
      location.type === 'COUNTY'
    ) {
      sortProfiles.unshift({
        name: 'Best Availability',
        value: 'Recommended',
      });
    } else {
      sortProfiles.unshift({
        name: 'Nearest',
        value: 'Nearest',
      });

      showRadialSearch = true;
    }
  } else if (defaultSort) {
    let sortIndex = sortProfiles.findIndex(sp => sp.value === defaultSort);

    if (sortIndex === -1) {
      sortProfiles.unshift({
        name: defaultSort,
        value: defaultSort,
      });

      sortIndex = 0;
    }

    defaultSortIndex = sortIndex;
  }

  const defaultOptions = {
    sortProfile: sortProfiles[defaultSortIndex].value,
  };

  if (locationData.state) {
    initialOptions.arrivalDate = locationData.state.arrivalDate;
  }

  const contentRef = useRef();
  const contentButtonRef = useRef();
  const searchWrapperRef = useRef();
  const filtersRef = useRef();
  const filtersButtonRef = useRef();
  const listViewButtonRef = useRef();
  const mapViewButtonRef = useRef();
  const sortRef = useRef();
  const toggleRef = useRef();
  const listRef = useRef();
  const mapRef = useRef();

  const [showLoader, setShowLoader] = useState(false);

  const [mapToggleRatings, setMapToggleRatings] = useState(false);

  const [options, setOptions] = useState({
    ...defaultOptions,
    ...initialOptions,
  });

  const {
    data: items,
    meta,
    isClientSide,
  } = useResults(initialResults, options, locationData, arePackages);

  useEffect(() => {
    if (items) {
      setShowLoader(false);
    }
  }, [items]);

  const handleContentButtonClick = () => {
    contentRef.current.classList.toggle('show-less-content');
    contentButtonRef.current.classList.toggle('show-less-content');

    const contentButton = contentButtonRef.current;
    const readMoreHtml = '... <span>read more</span>';
    const readLessHtml = '<span>read less</span>';

    if (contentButton.innerHTML === readMoreHtml) {
      contentButton.innerHTML = readLessHtml;
    } else {
      contentButton.innerHTML = readMoreHtml;
    }
  };

  const handleChange = e => {
    const form = e.currentTarget;
    const formName = form.name;
    const formData = new FormData(form);
    const newOptions = {
      ...options,
    };

    const radius = formData.get('radius');
    if (radius !== 'any') {
      newOptions.radius = radius;
    } else {
      delete newOptions.radius;
    }

    setShowLoader(true);

    if (formName === 'spa-filters') {
      const filters = {};

      formData.forEach((value, key) => {
        if (key === 'radius' || value === 'any') {
          return null;
        } else {
          return (filters[key] = value === 'on' ? 1 : value);
        }
      });

      if (isObjEmpty(filters) && isObjEmpty(initialOptions.filters)) {
        delete newOptions.filters;
      } else {
        newOptions.filters = {
          ...filters,
          ...initialOptions.filters,
        };
      }

      window.dataLayer.push({
        event: 'filterselect',
        filterType: e.target.name,
        filters: newOptions.filters,
      });
    } else if (formName === 'spa-sort') {
      newOptions.sortProfile = formData.get('spa-sort-profile');
    }

    setOptions(newOptions);

    if (meta?.metadata) {
      navigate(baseUrl);
    } else {
      window.history.pushState({}, '', baseUrl);
    }
  };

  const handleOptionsButtonClick = option => {
    if (option === 'filters') {
      filtersRef.current.classList.toggle('hide-filters-mobile');
      filtersButtonRef.current.classList.toggle('active-button');
    } else if (option === 'list-view') {
      sortRef.current.classList.remove('hide-sort');
      toggleRef.current.classList.add('hide-toggle');
      listRef.current.classList.remove('hide-list');
      mapRef.current.classList.add('hide-map');
      mapViewButtonRef.current.classList.remove('active-button');
      listViewButtonRef.current.classList.add('active-button');
    } else if (option === 'map-view') {
      toggleRef.current.classList.remove('hide-toggle');
      sortRef.current.classList.add('hide-sort');
      mapRef.current.classList.remove('hide-map');
      listRef.current.classList.add('hide-list');
      listViewButtonRef.current.classList.remove('active-button');
      mapViewButtonRef.current.classList.add('active-button');
    }
  };

  return (
    <>
      <StyledSpaListing>
        {!isSpaGroupListing && (
          <>
            <StyledBreadcrumbs items={breadcrumbs} isSpaListing={true} />
            <Container>
              <StyledSummary
                onClick={() =>
                  searchWrapperRef.current.classList.toggle(
                    'hide-search-mobile',
                  )
                }
              >
                <StyledSummaryHeading as={isGenericSpaListingPage && 'h2'}>
                  {heading}
                </StyledSummaryHeading>
                {options.arrivalDate && (
                  <StyledSummaryText>
                    {formatDate(options.arrivalDate, true)}
                  </StyledSummaryText>
                )}
                <StyledSummaryIcon image={searchIcon} />
              </StyledSummary>
              {content && content.topContent && (
                <StyledContent className="show-less-content" ref={contentRef}>
                  <StyledContentText>
                    <span
                      dangerouslySetInnerHTML={{ __html: content.topContent }}
                    />
                    <StyledContentButton
                      className="show-less-content"
                      ref={contentButtonRef}
                      onClick={() => handleContentButtonClick()}
                    >
                      ... <span>read more</span>
                    </StyledContentButton>
                  </StyledContentText>
                </StyledContent>
              )}
              <StyledSearchWrapper
                className="hide-search-mobile"
                ref={searchWrapperRef}
              >
                <SpaSearch
                  searchFields={searchFields}
                  searchType={options.searchType}
                  location={location}
                  arrivalDate={options.arrivalDate}
                  baseUrl={baseUrl}
                  isGenericSpaListingPage={isGenericSpaListingPage}
                />
              </StyledSearchWrapper>
            </Container>
          </>
        )}
        <StyledFiltersList>
          <Container>
            <StyledInner>
              <StyledFiltersWrapper
                className="hide-filters-mobile"
                ref={filtersRef}
              >
                <SpaFilters
                  meta={meta}
                  showRadialSearch={showRadialSearch}
                  arePackages={arePackages}
                  handleChange={handleChange}
                />
              </StyledFiltersWrapper>
              <StyledViews>
                <StyledOptions>
                  <StyledButton
                    ref={filtersButtonRef}
                    filters={true}
                    onClick={() => handleOptionsButtonClick('filters')}
                  >
                    <Svg image={filterIcon} />
                    <span>Filters</span>
                  </StyledButton>
                  <StyledButtons>
                    <StyledButton
                      className="active-button"
                      ref={listViewButtonRef}
                      onClick={() => handleOptionsButtonClick('list-view')}
                    >
                      <Svg image={listIcon} />
                      <span>List View</span>
                    </StyledButton>
                    <StyledButton
                      ref={mapViewButtonRef}
                      onClick={() => handleOptionsButtonClick('map-view')}
                    >
                      <Svg image={mapIcon} />
                      <span>Map View</span>
                    </StyledButton>
                  </StyledButtons>
                  <StyledSort
                    ref={sortRef}
                    name="spa-sort"
                    onChange={e => handleChange(e)}
                  >
                    <StyledSelectWrap>
                      <StyledLabel>Sort</StyledLabel>
                      <StyledSelect name="spa-sort-profile" required>
                        {sortProfiles.map(({ name, value }, id) => (
                          <option
                            key={id}
                            value={value}
                            selected={id === defaultSortIndex}
                          >
                            {name}
                          </option>
                        ))}
                      </StyledSelect>
                    </StyledSelectWrap>
                  </StyledSort>
                  <StyledToggle className="hide-toggle" ref={toggleRef}>
                    <StyledToggleText>See Prices</StyledToggleText>
                    <StyledToggleWrapper>
                      <StyledToggleInput
                        type="checkbox"
                        onClick={() => setMapToggleRatings(!mapToggleRatings)}
                      />
                      <StyledToggleControl />
                    </StyledToggleWrapper>
                    <StyledToggleText>
                      See Ratings{' '}
                      <StyledToggleInfo
                        aria-label="Ratings powered by Trustpilot"
                        data-label="Ratings powered by Trustpilot"
                      >
                        i
                      </StyledToggleInfo>
                    </StyledToggleText>
                  </StyledToggle>
                </StyledOptions>
                <StyledListWrapper ref={listRef}>
                  <SpaList
                    heading="Spa Results"
                    items={items}
                    baseUrl={baseUrl}
                    meta={meta}
                    arePackages={arePackages}
                    isClientSide={isClientSide}
                  />
                </StyledListWrapper>
                <StyledMapWrapper className="hide-map" ref={mapRef}>
                  <LocationMap
                    locations={
                      arePackages
                        ? items &&
                          items.map(item => ({
                            ...item,
                            city: item.spa.city,
                            county: item.spa.county,
                            location: item.spa.location,
                            spaRating: item.spa.spaRating,
                          }))
                        : items
                    }
                    marker={'price'}
                    mapZoom={!options.locationId && 6}
                    mapToggleRatings={mapToggleRatings}
                    isSpaListing={true}
                  />
                </StyledMapWrapper>
              </StyledViews>
            </StyledInner>
          </Container>
        </StyledFiltersList>
        <RecentlyViewed />
        {content && content.bottomContent && (
          <Content html={content.bottomContent} contain={true} />
        )}
        {contentBlocks && contentBlocks.length > 0 && (
          <Wrapper gradientBackground={true}>
            <ModularBlocks items={contentBlocks} />
          </Wrapper>
        )}
        <OtherSpaLocations location={location} items={otherLocations} />
        <TrustpilotWidget type="carousel" isTest={true} />
      </StyledSpaListing>
      {showLoader && <Loader />}
    </>
  );
};

export default SpaListing;

export const SpaListingMetaFragment = graphql`
  fragment SpaListingMetaFragment on SPASEEKERS_SetFacetArrayMetaGraphType {
    currentPage
    pageSize
    totalItems
    totalPages
    metadata {
      group
      value
      total
      filtered
    }
  }
`;
